/**
 * Helpers for turning API/Axios errors into user-facing messages.
 */

interface ValidationDetail {
  field?: string;
  message?: string;
}

interface ApiErrorShape {
  response?: {
    status?: number;
    data?: {
      // The AppError handler returns a nested object: { error: { message } };
      // some older endpoints return a flat string or a top-level message.
      error?: string | { message?: string; details?: ValidationDetail[] };
      message?: string;
      // The Joi validation middleware returns per-field details alongside the
      // generic "Validation failed" string.
      details?: ValidationDetail[];
    };
  };
}

/** Join per-field validation messages into a single readable sentence. */
const formatDetails = (details: ValidationDetail[] | undefined): string | null => {
  if (!Array.isArray(details)) return null;
  const messages = details.map((d) => d.message?.trim()).filter((m): m is string => Boolean(m));
  return messages.length ? messages.join(' ') : null;
};

/**
 * Extracts a user-facing message from an API error, checking the shapes the
 * backend uses: { error: 'Validation failed', details: [...] } (Joi),
 * { error: { message } } (AppError), { error: <string> }, { message }.
 * Field-level validation details take priority over the generic top-level
 * message so the user sees what's actually wrong (bug 870). Returns `fallback`
 * when none is present — never the raw Axios "Request failed with status code N".
 */
export const getApiErrorMessage = (error: unknown, fallback: string): string => {
  const data = (error as ApiErrorShape | undefined)?.response?.data;
  if (data) {
    const nested = data.error;
    // Prefer per-field details (top-level or nested) over the generic string.
    const detailMessage =
      formatDetails(data.details) ??
      (nested && typeof nested === 'object' ? formatDetails(nested.details) : null);
    if (detailMessage) return detailMessage;

    if (typeof nested === 'string' && nested.trim()) return nested;
    if (nested && typeof nested === 'object' && typeof nested.message === 'string' && nested.message.trim()) {
      return nested.message;
    }
    if (typeof data.message === 'string' && data.message.trim()) return data.message;
  }
  return fallback;
};

/**
 * Per-field messages from an API error, keyed by the request field name.
 *
 * Reads the Joi validation body `{ details: [...] }` first and, when that is
 * absent, the AppError body `{ error: { details: [...] } }` (409 duplicate
 * contact_id, the D-008 default-division 400). Entries without a non-empty
 * `field` and `message` are skipped; the first message per field wins.
 * Returns `{}` for any other shape, so callers fall back to a toast.
 */
export const getApiFieldErrors = (error: unknown): Record<string, string> => {
  const data = (error as ApiErrorShape | undefined)?.response?.data;
  if (!data || typeof data !== 'object') return {};

  const nested = data.error;
  const details = Array.isArray(data.details)
    ? data.details
    : nested && typeof nested === 'object' && Array.isArray(nested.details)
      ? nested.details
      : [];

  const fieldErrors: Record<string, string> = {};
  for (const detail of details) {
    if (!detail || typeof detail !== 'object') continue;
    const field = typeof detail.field === 'string' ? detail.field.trim() : '';
    const message = typeof detail.message === 'string' ? detail.message.trim() : '';
    if (!field || !message || field in fieldErrors) continue;
    fieldErrors[field] = message;
  }
  return fieldErrors;
};

export interface SplitApiFieldErrors {
  fieldErrors: Record<string, string>;
  residualMessages: string[];
}

/**
 * Splits Joi/AppError field details into those that belong on visible form
 * fields and leftover messages (unmapped fields) for a banner or toast.
 */
export const splitApiFieldErrors = (
  error: unknown,
  visibleFieldNames: readonly string[]
): SplitApiFieldErrors => {
  const shown = new Set(visibleFieldNames);
  const fieldErrors: Record<string, string> = {};
  const residualMessages: string[] = [];
  for (const [field, message] of Object.entries(getApiFieldErrors(error))) {
    if (shown.has(field)) {
      fieldErrors[field] = message;
    } else {
      residualMessages.push(message);
    }
  }
  return { fieldErrors, residualMessages };
};

/**
 * HTTP status code from an API error, when the request actually reached the
 * server. Returns undefined for network/transport failures.
 */
export const getApiErrorStatus = (error: unknown): number | undefined =>
  (error as ApiErrorShape | undefined)?.response?.status;

/**
 * Wraps a backend-provided message in an Error shaped like an Axios error
 * response, so it flows through `getApiErrorMessage` the same way a real
 * HTTP error would. Useful when a 2xx response embeds a per-item failure
 * (e.g. a batch endpoint returning `{ errors: [{ error }] }`) that still
 * needs to reach the user as a specific message instead of a generic one.
 */
export const toApiError = (message: string): Error => {
  const error = new Error(message) as Error & { response: { data: { error: string } } };
  error.response = { data: { error: message } };
  return error;
};
